import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Quote, QuoteLineItem, QuoteVersion } from '@/types/quote';
import { useToast } from '@/hooks/use-toast';

export const useQuote = (quoteId: string) => {
  const [quote, setQuote] = useState<Quote | null>(null);
  const [lineItems, setLineItems] = useState<QuoteLineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved');
  const { toast } = useToast();
  const pendingSaves = useRef<Set<string>>(new Set());

  const fetchQuote = async () => {
    try {
      setLoading(true);
      const [quoteResult, itemsResult] = await Promise.all([
        supabase
          .from('quotes')
          .select(`
            *,
            organization:organizations(id, name, kvk, btw, phone, email, addresses),
            contact:contacts(id, first_name, last_name, email, phone, mobile, title),
            project:projects(id, name, project_number),
            charging_location:charging_locations(id, name),
            charging_opportunity:charging_opportunities(id, name)
          `)
          .eq('id', quoteId)
          .single(),
        supabase
          .from('quote_line_items')
          .select('*')
          .eq('quote_id', quoteId)
          .order('position'),
      ]);

      if (quoteResult.error) throw quoteResult.error;
      if (itemsResult.error) throw itemsResult.error;

      setQuote(quoteResult.data as unknown as Quote);
      setLineItems(itemsResult.data || []);
      setSaveStatus('saved');
    } catch (error: any) {
      console.error('Error fetching quote:', error);
      toast({
        title: 'Fout bij laden',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateQuote = async (updates: Partial<Quote>, options?: { silent?: boolean; refetch?: boolean }) => {
    try {
      setSaveStatus('saving');
      
      // Validate quote_number uniqueness
      if (updates.quote_number && updates.quote_number !== quote?.quote_number) {
        const { data: existing } = await supabase
          .from('quotes')
          .select('id')
          .eq('quote_number', updates.quote_number)
          .neq('id', quoteId)
          .is('deleted_at', null)
          .maybeSingle();

        if (existing) {
          toast({ title: `Offertenummer ${updates.quote_number} bestaat al`, variant: 'destructive' });
          setSaveStatus('unsaved');
          return;
        }
      }

      const { error } = await supabase
        .from('quotes')
        .update(updates as Record<string, unknown>)
        .eq('id', quoteId);

      if (error) throw error;

      if (!options?.silent) {
        toast({ title: 'Offerte opgeslagen' });
      }

      if (options?.refetch === false) {
        setQuote(prev => prev ? { ...prev, ...updates, updated_at: new Date().toISOString() } : null);
      } else {
        await fetchQuote();
      }
      setSaveStatus('saved');
    } catch (error: any) {
      console.error('Error updating quote:', error);
      setSaveStatus('unsaved');
      toast({
        title: 'Fout bij opslaan',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Optimistic add with immediate UI update
  const addLineItem = useCallback(async (item: Omit<QuoteLineItem, 'id' | 'created_at' | 'updated_at' | 'subtotal' | 'vat_amount' | 'total'>) => {
    const tempId = `temp-${Date.now()}`;
    // Prijs is inclusief BTW - bereken BTW terug
    const total = item.quantity * item.unit_price;
    const vatAmount = total * (item.vat_rate / (100 + item.vat_rate));
    const subtotal = total - vatAmount;
    
    // Optimistic update
    const optimisticItem: QuoteLineItem = {
      ...item,
      id: tempId,
      subtotal,
      vat_amount: vatAmount,
      total,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    
    setLineItems(prev => [...prev, optimisticItem]);
    setSaveStatus('saving');
    pendingSaves.current.add(tempId);
    
    try {
      const { data, error } = await supabase
        .from('quote_line_items')
        .insert(item)
        .select()
        .single();

      if (error) throw error;
      
      // Replace temp item with real one
      setLineItems(prev => prev.map(i => i.id === tempId ? data : i));
      pendingSaves.current.delete(tempId);
      
      if (pendingSaves.current.size === 0) {
        setSaveStatus('saved');
      }
      
      // Update quote totals
      await syncQuoteTotals();
    } catch (error: any) {
      console.error('Error adding line item:', error);
      // Rollback
      setLineItems(prev => prev.filter(i => i.id !== tempId));
      pendingSaves.current.delete(tempId);
      setSaveStatus('unsaved');
      toast({
        title: 'Fout bij toevoegen',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  }, [quoteId]);

  // Optimistic update without refetch
  const updateLineItem = useCallback(async (itemId: string, updates: Partial<QuoteLineItem>) => {
    // Get current item for rollback
    const originalItem = lineItems.find(i => i.id === itemId);
    if (!originalItem) return;
    
    // Calculate new totals if needed
    let calculatedUpdates = { ...updates };
    if ('quantity' in updates || 'unit_price' in updates || 'vat_rate' in updates) {
      const quantity = updates.quantity ?? originalItem.quantity;
      const unitPrice = updates.unit_price ?? originalItem.unit_price;
      const vatRate = updates.vat_rate ?? originalItem.vat_rate;
      // Prijs is inclusief BTW - bereken BTW terug
      const total = quantity * unitPrice;
      const vatAmount = total * (vatRate / (100 + vatRate));
      const subtotal = total - vatAmount;
      calculatedUpdates = {
        ...calculatedUpdates,
        subtotal,
        vat_amount: vatAmount,
        total,
      };
    }
    
    // Optimistic update
    setLineItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, ...calculatedUpdates } : item
    ));
    
    setSaveStatus('saving');
    pendingSaves.current.add(itemId);
    
    try {
      const { error } = await supabase
        .from('quote_line_items')
        .update(updates)
        .eq('id', itemId);

      if (error) throw error;
      
      pendingSaves.current.delete(itemId);
      
      if (pendingSaves.current.size === 0) {
        setSaveStatus('saved');
        // Sync quote totals after all saves complete
        await syncQuoteTotals();
      }
    } catch (error: any) {
      console.error('Error updating line item:', error);
      // Rollback
      setLineItems(prev => prev.map(item => 
        item.id === itemId ? originalItem : item
      ));
      pendingSaves.current.delete(itemId);
      setSaveStatus('unsaved');
      toast({
        title: 'Fout bij aanpassen',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  }, [lineItems]);

  // Optimistic delete
  const deleteLineItem = useCallback(async (itemId: string) => {
    const originalItem = lineItems.find(i => i.id === itemId);
    if (!originalItem) return;
    
    // Optimistic update
    setLineItems(prev => prev.filter(i => i.id !== itemId));
    setSaveStatus('saving');
    
    try {
      const { error } = await supabase
        .from('quote_line_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
      
      setSaveStatus('saved');
      // Sync quote totals
      await syncQuoteTotals();
    } catch (error: any) {
      console.error('Error deleting line item:', error);
      // Rollback
      setLineItems(prev => [...prev, originalItem].sort((a, b) => a.position - b.position));
      setSaveStatus('unsaved');
      toast({
        title: 'Fout bij verwijderen',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  }, [lineItems]);

  // Duplicate line item
  const duplicateLineItem = useCallback(async (itemId: string) => {
    const originalItem = lineItems.find(i => i.id === itemId);
    if (!originalItem) return;
    
    await addLineItem({
      quote_id: originalItem.quote_id,
      position: lineItems.length,
      description: originalItem.description,
      quantity: originalItem.quantity,
      unit_price: originalItem.unit_price,
      vat_rate: originalItem.vat_rate,
    });
  }, [lineItems, addLineItem]);

  // Update line items order (for drag-and-drop)
  // Two-step update to prevent unique constraint conflicts
  const reorderLineItems = useCallback(async (reorderedItems: QuoteLineItem[]) => {
    const originalItems = [...lineItems];
    
    // Optimistic update with new positions
    const itemsWithNewPositions = reorderedItems.map((item, index) => ({
      ...item,
      position: index,
    }));
    setLineItems(itemsWithNewPositions);
    setSaveStatus('saving');
    
    try {
      // Step 1: Move all items to temporary high-offset positions to avoid conflicts
      for (let i = 0; i < itemsWithNewPositions.length; i++) {
        const { error } = await supabase
          .from('quote_line_items')
          .update({ position: 10000 + i })
          .eq('id', itemsWithNewPositions[i].id);
        if (error) throw error;
      }
      
      // Step 2: Set final positions
      for (let i = 0; i < itemsWithNewPositions.length; i++) {
        const { error } = await supabase
          .from('quote_line_items')
          .update({ position: i })
          .eq('id', itemsWithNewPositions[i].id);
        if (error) throw error;
      }
      
      setSaveStatus('saved');
    } catch (error: any) {
      console.error('Error reordering line items:', error);
      setLineItems(originalItems);
      setSaveStatus('unsaved');
      toast({
        title: 'Fout bij herschikken',
        description: error.message,
        variant: 'destructive',
      });
    }
  }, [lineItems]);

  // Sync quote totals from line items
  const syncQuoteTotals = useCallback(async () => {
    // Get fresh line items from DB to ensure accuracy
    const { data: items } = await supabase
      .from('quote_line_items')
      .select('subtotal, vat_amount, total')
      .eq('quote_id', quoteId);
    
    if (!items) return;
    
    const subtotal = items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
    const vatAmount = items.reduce((sum, item) => sum + (item.vat_amount || 0), 0);
    const total = items.reduce((sum, item) => sum + (item.total || 0), 0);
    
    await supabase
      .from('quotes')
      .update({
        subtotal_amount: subtotal,
        vat_amount: vatAmount,
        total_amount: total,
      })
      .eq('id', quoteId);
    
    // Update local state
    setQuote(prev => prev ? {
      ...prev,
      subtotal_amount: subtotal,
      vat_amount: vatAmount,
      total_amount: total,
      updated_at: new Date().toISOString(),
    } : null);
  }, [quoteId]);

  // Restore quote to a previous version
  const restoreVersion = useCallback(async (version: QuoteVersion) => {
    try {
      setSaveStatus('saving');
      
      // 1. Update quote with version data
      const { error: updateError } = await supabase
        .from('quotes')
        .update({
          title: version.title,
          body: version.body,
          terms: version.terms,
          subtotal_amount: version.subtotal_amount,
          vat_amount: version.vat_amount,
          total_amount: version.total_amount,
        })
        .eq('id', quoteId);

      if (updateError) throw updateError;

      // 2. Delete current line items
      const { error: deleteError } = await supabase
        .from('quote_line_items')
        .delete()
        .eq('quote_id', quoteId);

      if (deleteError) throw deleteError;

      // 3. Restore line items from version
      if (version.line_items && Array.isArray(version.line_items) && version.line_items.length > 0) {
        // subtotal, vat_amount en total zijn gegenereerde kolommen: niet meeschrijven.
        const items = version.line_items.map((item: any) => ({
          quote_id: quoteId,
          position: item.position,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          vat_rate: item.vat_rate,
        }));
        
        const { error: insertError } = await supabase
          .from('quote_line_items')
          .insert(items);

        if (insertError) throw insertError;
      }

      toast({
        title: 'Versie hersteld',
        description: `Offerte is teruggezet naar versie ${version.version_number}`,
      });

      await fetchQuote();
      setSaveStatus('saved');
    } catch (error: any) {
      console.error('Error restoring version:', error);
      setSaveStatus('unsaved');
      toast({
        title: 'Fout bij herstellen',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  }, [quoteId, toast]);

  useEffect(() => {
    if (quoteId) {
      fetchQuote();
    }
  }, [quoteId]);

  // Calculate live totals from lineItems state
  const calculatedTotals = {
    subtotal: lineItems.reduce((sum, item) => sum + (item.subtotal || 0), 0),
    vat: lineItems.reduce((sum, item) => sum + (item.vat_amount || 0), 0),
    total: lineItems.reduce((sum, item) => sum + (item.total || 0), 0),
  };

  return {
    quote,
    lineItems,
    loading,
    saveStatus,
    calculatedTotals,
    updateQuote,
    addLineItem,
    updateLineItem,
    deleteLineItem,
    duplicateLineItem,
    reorderLineItems,
    restoreVersion,
    refetch: fetchQuote,
  };
};
